/**
 * 问卷表单配置文件
 *
 * 该文件定义了KYC流程中问卷表单的配置结构。
 * 包含两个主要部分：
 *
 * 1. 个人问卷配置 (INDIVIDUAL_QUESTIONNAIRE_CONFIG)
 *    - 个人信息 - 国籍、政治敏感人物、年收入、净资产等
 *    - 投资信息 - 投资经验、产品知识、投资目标、风险承受能力等
 *    - 补充信息 - 财富来源、资金方法、预期交易量、就业状况等
 *
 * 2. 机构问卷配置 (INSTITUTION_QUESTIONNAIRE_CONFIG)
 *    - 预期存款金额、交易量、投资目标
 *    - 资金方法、资金来源国家、存款机构
 *    - 投资产品类型等
 *
 * 注意：所有文案使用硬编码，暂时不需要国际化配置
 */

import type { TFieldOrGroup, TInstitutionQuestionnaireFieldItem } from "@adgm/cross-models";
import { kycCountrySelectOptionList, i18nCrossUtil, createOptions } from "@adgm/libs";
import { CUSTOM_RADIO_WITH_INPUT, CUSTOM_RADIO_WITH_SELECT } from "@adgm/cross-config";

const intl = i18nCrossUtil.t();

// 依赖employmentStatus的字段列表
export const EMPLOYMENT_STATUS_DEPENDENT_FIELDS = [
  "employerName",
  "employerCountry",
  "employerIndustry",
];

// 需要employmentStatus为必填的状态列表
export const REQUIRED_EMPLOYMENT_STATUSES = [
  intl["kyc.v2.option.label.salaried.government"],
  intl["kyc.v2.option.label.salaried.private"],
  intl["kyc.v2.option.label.self.employed"],
  intl["kyc.v2.option.label.business.owner"],
];

// categoryRadioGroup 字段选项配置
const YEARS_OF_INVESTMENT_EXPERIENCE_OPTIONS = createOptions([
  intl["kyc.v2.option.label.less.than.1.year"],
  intl["kyc.v2.option.label.1.to.2.years"],
  intl["kyc.v2.option.label.2.to.5.years"],
  intl["kyc.v2.option.label.5.to.8.years"],
  intl["kyc.v2.option.label.more.than.8.years"],
  intl["kyc.v2.option.label.no.investment.experience"],
]);

const NUMBER_OF_INVESTMENTS_LAST_FIVE_YEAR_OPTIONS = createOptions([
  intl["kyc.v2.option.label.1.to.10"],
  intl["kyc.v2.option.label.10.to.20"],
  intl["kyc.v2.option.label.20.plus"],
  intl["kyc.v2.option.label.none"],
]);

// 个人问卷嵌套配置
export const INDIVIDUAL_QUESTIONNAIRE_CONFIG: TFieldOrGroup[] = [
  {
    type: "group",
    name: "personalInformation",
    label: intl["kyc.v2.questionnaire.personalInformation.label"],
    fields: [
      {
        label: intl["kyc.v2.questionnaire.personalInformation.multipleNationalities.label"],
        name: "multipleNationalities",
        options: [
          ...createOptions([intl["kyc.v2.option.label.no"]]),
          { title: intl["kyc.v2.option.label.please.select"], value: CUSTOM_RADIO_WITH_SELECT },
        ],
        type: "radioWithSelect",
        subOptions: kycCountrySelectOptionList("commonName"),
        validation: [{ required: true }],
        fieldSequenceNumber: 1,
      },
      {
        label: intl["kyc.v2.questionnaire.personalInformation.politicallyExposedPerson.label"],
        name: "politicallyExposedPerson",
        options: createOptions([intl["kyc.v2.option.label.yes"], intl["kyc.v2.option.label.no"]]),
        type: "radio",
        validation: [{ required: true }],
        fieldSequenceNumber: 2,
      },
      {
        label: intl["kyc.v2.questionnaire.personalInformation.annualIncome.label"],
        name: "annualIncome",
        options: createOptions(["<100K", "100K - 200K", "200K - 400K", "400K - 600K", ">600K"]),
        type: "radio",
        validation: [{ required: true }],
        fieldSequenceNumber: 3,
      },
      {
        label: intl["kyc.v2.questionnaire.personalInformation.liquidNetWorth.label"],
        name: "liquidNetWorth",
        options: createOptions(["<1M", "1M-2M", "2M-4M", "4M-6M", ">6M"]),
        type: "radio",
        validation: [{ required: true }],
        fieldSequenceNumber: 4,
      },
    ],
  },
  {
    type: "group",
    name: "investment",
    label: intl["kyc.v2.questionnaire.investment.label"],
    fields: [
      {
        label: intl["kyc.v2.questionnaire.investment.yearsOfInvestmentExperience.label"],
        name: "yearsOfInvestmentExperience",
        type: "categoryRadioGroup",
        categoryOptions: [
          {
            key: intl["kyc.v2.option.label.fund.products"],
            options: YEARS_OF_INVESTMENT_EXPERIENCE_OPTIONS,
          },
          {
            key: intl["kyc.v2.option.label.virtual.assets"],
            options: YEARS_OF_INVESTMENT_EXPERIENCE_OPTIONS,
          },
          {
            key: intl["kyc.v2.option.label.equities"],
            options: YEARS_OF_INVESTMENT_EXPERIENCE_OPTIONS,
          },
          {
            key: intl["kyc.v2.option.label.derivatives"],
            options: YEARS_OF_INVESTMENT_EXPERIENCE_OPTIONS,
          },
        ],
        validation: [{ required: true }],
        fieldSequenceNumber: 5,
      },
      {
        label: intl["kyc.v2.questionnaire.investment.numberOfInvestmentsLastFiveYear.label"],
        name: "numberOfInvestmentsLastFiveYear",
        type: "categoryRadioGroup",
        categoryOptions: [
          {
            key: intl["kyc.v2.option.label.fund.products"],
            options: NUMBER_OF_INVESTMENTS_LAST_FIVE_YEAR_OPTIONS,
          },
          {
            key: intl["kyc.v2.option.label.virtual.assets"],
            options: NUMBER_OF_INVESTMENTS_LAST_FIVE_YEAR_OPTIONS,
          },
          {
            key: intl["kyc.v2.option.label.equities"],
            options: NUMBER_OF_INVESTMENTS_LAST_FIVE_YEAR_OPTIONS,
          },
          {
            key: intl["kyc.v2.option.label.derivatives"],
            options: NUMBER_OF_INVESTMENTS_LAST_FIVE_YEAR_OPTIONS,
          },
        ],
        validation: [{ required: true }],
        fieldSequenceNumber: 6,
      },
      {
        label: intl["kyc.v2.questionnaire.investment.productKnowledgeLevels.label"],
        name: "productKnowledgeLevels",
        options: createOptions([
          intl["kyc.v2.option.label.professional.certification"],
          intl["kyc.v2.option.label.work.experience.financial"],
          intl["kyc.v2.option.label.degree.finance"],
          intl["kyc.v2.option.label.trading.courses"],
          intl["kyc.v2.option.label.no.formal.training"],
        ]),
        type: "checkbox",
        validation: [{ required: true }],
        fieldSequenceNumber: 7,
      },
      {
        label: intl["kyc.v2.questionnaire.investment.expectedInvestmentHoldingPeriod.label"],
        name: "expectedInvestmentHoldingPeriod",
        options: createOptions([
          intl["kyc.v2.option.label.less.than.6.months"],
          intl["kyc.v2.option.label.6.months.to.1.year"],
          intl["kyc.v2.option.label.1.to.3.years"],
          intl["kyc.v2.option.label.3.to.5.years"],
          intl["kyc.v2.option.label.more.than.5.years"],
        ]),
        type: "radio",
        validation: [{ required: true }],
        fieldSequenceNumber: 8,
      },
      {
        label: intl["kyc.v2.questionnaire.investment.expectedAccountUtilization.label"],
        name: "expectedAccountUtilization",
        options: [
          ...createOptions([
            intl["kyc.v2.option.label.income.preference"],
            intl["kyc.v2.option.label.capital.growth"],
            intl["kyc.v2.option.label.market.speculation"],
            intl["kyc.v2.option.label.offramp.digital.assets"],
            intl["kyc.v2.option.label.park.holdings"],
          ]),
          { title: intl["kyc.v2.option.label.other"], value: CUSTOM_RADIO_WITH_INPUT },
        ],
        type: "radioWithInput",
        validation: [{ required: true }],
        fieldSequenceNumber: 9,
      },
      // {
      //   label: intl["kyc.v2.questionnaire.investment.investmentLossToleranceLevel.label"],
      //   name: "investmentLossToleranceLevel",
      //   options: createOptions(["0%", "0% - 20%", "20% - 40%", "40% - 60%", ">60%"]),
      //   type: "radio",
      //   validation: [{ required: true }],
      //   fieldSequenceNumber: 10,
      // },
    ],
  },
  {
    type: "group",
    name: "supplement",
    label: intl["kyc.v2.questionnaire.supplement.label"],
    fields: [
      {
        label: intl["kyc.v2.questionnaire.supplement.sourceOfFunds.label"],
        name: "sourceOfFund",
        type: "individualSourceOfFund",
        validation: [{ required: true }],
        fieldSequenceNumber: 10,
      },
      // {
      //   label: intl["kyc.v2.questionnaire.supplement.natureOfFunds.label"],
      //   name: "natureOfFunds",
      //   type: "input",
      //   validation: [{ required: true }],
      //   fieldSequenceNumber: 12,
      // },
      {
        label: intl["kyc.v2.questionnaire.supplement.sourceOfWealth.label"],
        name: "sourceOfWealth",
        type: "individualSourceOfWealth",
        validation: [{ required: true }],
        fieldSequenceNumber: 11,
      },
      // {
      //   label: intl["kyc.v2.questionnaire.supplement.natureOfWealth.label"],
      //   name: "natureOfWealth",
      //   type: "input",
      //   validation: [{ required: true }],
      //   fieldSequenceNumber: 14,
      // },
      {
        label: intl["kyc.v2.questionnaire.supplement.employmentStatus.label"],
        name: "employmentStatus",
        options: [
          ...createOptions([
            ...REQUIRED_EMPLOYMENT_STATUSES,
            intl["kyc.v2.option.label.student"],
            intl["kyc.v2.option.label.housewife.dependent"],
            intl["kyc.v2.option.label.retired"],
            intl["kyc.v2.option.label.unemployed"],
          ]),
          { title: intl["kyc.v2.option.label.other"], value: CUSTOM_RADIO_WITH_INPUT },
        ],
        type: "radioWithInput",
        validation: [{ required: true }],
        fieldSequenceNumber: 12,
      },
      {
        label: intl["kyc.v2.questionnaire.supplement.employerName.label"],
        name: "employerName",
        type: "input",
        validation: [{ required: true }],
        fieldSequenceNumber: 13,
      },
      {
        label: intl["kyc.v2.questionnaire.supplement.employerCountry.label"],
        name: "employerCountry",
        options: kycCountrySelectOptionList("commonName"),
        type: "select",
        validation: [{ required: true }],
        fieldSequenceNumber: 14,
      },
      {
        label: intl["kyc.v2.questionnaire.supplement.employerIndustry.label"],
        name: "employerIndustry",
        options: [
          ...createOptions([
            intl["kyc.v2.option.label.crypto.web3"],
            intl["kyc.v2.option.label.technology.it"],
            intl["kyc.v2.option.label.financial.services"],
            intl["kyc.v2.option.label.legal.professional"],
            intl["kyc.v2.option.label.real.estate"],
            intl["kyc.v2.option.label.manufacturing.industrial"],
            intl["kyc.v2.option.label.government.public"],
            intl["kyc.v2.option.label.retail.consumer"],
            intl["kyc.v2.option.label.hospitality.travel"],
            intl["kyc.v2.option.label.media.entertainment"],
            intl["kyc.v2.option.label.education"],
            intl["kyc.v2.option.label.healthcare.pharmaceuticals"],
            intl["kyc.v2.option.label.family.officer"],
          ]),
          { title: intl["kyc.v2.option.label.other"], value: CUSTOM_RADIO_WITH_INPUT },
        ],
        type: "radioWithInput",
        validation: [{ required: true }],
        fieldSequenceNumber: 15,
      },
      {
        label: intl["kyc.v2.questionnaire.supplement.accountFundingMethods.label"],
        name: "accountFundingMethods",
        options: [
          ...createOptions([
            intl["kyc.v2.option.label.transfer.personal.bank"],
            intl["kyc.v2.option.label.transfer.corporate.bank"],
            intl["kyc.v2.option.label.transfer.brokerage"],
            intl["kyc.v2.option.label.transfer.personal.wallet"],
            intl["kyc.v2.option.label.transfer.corporate.wallet"],
          ]),
          { title: intl["kyc.v2.option.label.other"], value: CUSTOM_RADIO_WITH_INPUT },
        ],
        type: "checkboxWithInput",
        validation: [{ required: true }],
        fieldSequenceNumber: 16,
      },
      {
        label: intl["kyc.v2.questionnaire.supplement.fundingAccountOwnerName.label"],
        name: "fundingAccountOwnerName",
        type: "input",
        validation: [{ required: true }],
        fieldSequenceNumber: 17,
      },
      {
        label: intl["kyc.v2.questionnaire.supplement.intendedDepositAmount.label"],
        name: "intendedDepositAmount",
        options: createOptions([
          intl["kyc.v2.option.label.less.than.1.million"],
          intl["kyc.v2.option.label.between.1.to.5.million"],
          intl["kyc.v2.option.label.more.than.5.million"],
        ]),
        type: "radio",
        validation: [{ required: true }],
        fieldSequenceNumber: 18,
      },
      {
        label: intl["kyc.v2.questionnaire.supplement.expectedTradingVolume.label"],
        name: "expectedTradingVolume",
        options: createOptions([
          intl["kyc.v2.option.label.less.than.1.million"],
          intl["kyc.v2.option.label.between.1.to.5.million"],
          intl["kyc.v2.option.label.more.than.5.million"],
        ]),
        type: "radio",
        validation: [{ required: true }],
        fieldSequenceNumber: 19,
      },
      {
        label: intl["kyc.v2.questionnaire.supplement.fundingSourceCountry.label"],
        name: "fundingSourceCountry",
        options: kycCountrySelectOptionList("commonName"),
        type: "select",
        validation: [{ required: true }],
        fieldSequenceNumber: 20,
      },
      {
        label: intl["kyc.v2.questionnaire.supplement.fundingSourceInstitution.label"],
        name: "fundingSourceInstitution",
        type: "input",
        validation: [{ required: true }],
        fieldSequenceNumber: 21,
      },
      {
        label: intl["kyc.v2.questionnaire.supplement.desiredProductTypes.label"],
        name: "desiredProductTypes",
        options: createOptions([
          intl["kyc.v2.option.label.equities"],
          intl["kyc.v2.option.label.funds"],
          intl["kyc.v2.option.label.virtual.assets"],
          intl["kyc.v2.option.label.derivatives.options"],
          intl["kyc.v2.option.label.all.of.above"],
        ]),
        type: "checkbox",
        validation: [{ required: true }],
        fieldSequenceNumber: 22,
      },
    ],
  },
  {
    type: "group",
    name: "taxResidences",
    label: intl["kyc.v2.questionnaire.taxResidences.label"],
    fields: [
      {
        label: intl["kyc.v2.questionnaire.taxResidences.taxCitizenFromUS.label"],
        name: "taxCitizenFromUS",
        options: createOptions([intl["kyc.v2.option.label.yes"], intl["kyc.v2.option.label.no"]]),
        type: "radio",
        validation: [{ required: true }],
        fieldSequenceNumber: 23,
      },
      {
        label: intl["kyc.v2.questionnaire.taxResidences.taxResidenceList.label"],
        name: "taxResidenceList",
        type: "taxResidences",
        validation: [{ required: true }],
        fieldSequenceNumber: 24,
      },
    ],
  },
  {
    type: "group",
    name: "professionalExperience",
    label: intl["kyc.v2.questionnaire.professionalExperience.label"],
    fields: [
      {
        label: intl["kyc.v2.questionnaire.professionalExperience.linkedinProfile.label"],
        name: "linkedinProfile",
        type: "input",
        validation: [{ required: false }],
        fieldSequenceNumber: 25,
      },
    ],
  },
];

// 机构问卷字段配置（平铺结构）
export const INSTITUTION_QUESTIONNAIRE_CONFIG: TInstitutionQuestionnaireFieldItem[] = [
  {
    label: intl["kyc.v2.questionnaire.institution.intendedDepositAmount.label"],
    name: "intendedDepositAmount",
    options: createOptions([
      "Less than USD 1 million",
      "Between USD 1 million and USD 5 million",
      "More than USD 5 million",
    ]),
    type: "radio",
    validation: [{ required: true }],
    fieldSequenceNumber: 1,
  },
  {
    label: intl["kyc.v2.questionnaire.institution.expectedTradeVolume.label"],
    name: "expectedTradeVolume",
    options: createOptions([
      "Less than USD 1 million",
      "Between USD 1 million and USD 5 million",
      "More than USD 5 million",
    ]),
    type: "radio",
    validation: [{ required: true }],
    fieldSequenceNumber: 2,
  },
  {
    label: intl["kyc.v2.questionnaire.institution.expectedAccountUtilization.label"],
    name: "expectedAccountUtilization",
    options: [
      ...createOptions([
        intl["kyc.v2.option.label.income.preference"],
        intl["kyc.v2.option.label.capital.growth"],
        intl["kyc.v2.option.label.market.speculation"],
        intl["kyc.v2.option.label.park.holdings"],
        intl["kyc.v2.option.label.offramp.digital.assets.lowercase"],
      ]),
      { title: intl["kyc.v2.option.label.other"], value: CUSTOM_RADIO_WITH_INPUT },
    ],
    type: "checkboxWithInput",
    validation: [{ required: true }],
    fieldSequenceNumber: 3,
  },
  {
    label: intl["kyc.v2.questionnaire.institution.fundingMethods.label"],
    name: "fundingMethods",
    options: [
      ...createOptions([
        intl["kyc.v2.option.label.transfer.shareholder.personal.bank"],
        intl["kyc.v2.option.label.transfer.shareholder.corporate.bank"],
        intl["kyc.v2.option.label.transfer.brokerage.investment"],
        intl["kyc.v2.option.label.transfer.shareholder.personal.wallet"],
        intl["kyc.v2.option.label.transfer.corporate.wallet"],
      ]),
      { title: intl["kyc.v2.option.label.other"], value: CUSTOM_RADIO_WITH_INPUT },
    ],
    type: "checkboxWithInput",
    validation: [{ required: true }],
    fieldSequenceNumber: 4,
  },
  {
    label: intl["kyc.v2.questionnaire.institution.fundingAccountOwnerName.label"],
    name: "fundingAccountOwnerName",
    type: "input",
    validation: [{ required: true }],
    fieldSequenceNumber: 5,
  },
  {
    label: intl["kyc.v2.questionnaire.institution.fundingSourceCountry.label"],
    name: "fundingSourceCountry",
    options: kycCountrySelectOptionList("commonName"),
    type: "select",
    validation: [{ required: true }],
    fieldSequenceNumber: 6,
  },
  {
    label: intl["kyc.v2.questionnaire.institution.depositingInstitution.label"],
    name: "depositingInstitution",
    type: "input",
    validation: [{ required: true }],
    fieldSequenceNumber: 7,
  },
  {
    label: intl["kyc.v2.questionnaire.institution.investmentProductTypes.label"],
    name: "investmentProductTypes",
    options: createOptions([
      "Equities",
      "Funds",
      "Virtual Assets",
      "Derivatives (eg Options & Futures)",
      "All of the above",
    ]),
    type: "checkbox",
    validation: [{ required: true }],
    fieldSequenceNumber: 8,
  },
];
